import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBadge from './NotificationBadge';
import NotificationDropdown from './NotificationDropdown';
import ThemeToggle from '../ui/ThemeToggle';

import WhatsNewPanel, { getUnreadCount } from '../ui/WhatsNewPanel';
import HeaderSearchSlot from './palette/HeaderSearchSlot';
import HeaderLocationButton from './HeaderLocationButton';
import useViewportWidth from './tools/useViewportWidth';
import { RAIL_DEFAULT_BELOW } from '../../utils/sidebarLayout';
import BugReportForm from '../BugReportForm';
import AssistantHeaderButton from '../AIAssistant/AssistantHeaderButton';
import ThemeContext from '../../contexts/ThemeContext';
import ChangeLocationModal from '../auth/ChangeLocationModal';
/**
 * The top bar's background (polish 2, lap A9.1). Light keeps its gradient exactly as it was;
 * dark takes the dark elevated token, so there is no near-white left edge under the title.
 */
export function headerBackground(theme) {
  return theme === 'dark' ? 'var(--bg-elevated)' : 'linear-gradient(to right, #f8fafc, var(--bg-elevated))';
}

/**
 * The "Teletime" wordmark's colour (polish 3). It is text in the brand's dark blue, which was
 * dark-on-dark on the dark bar; dark takes the dark theme's info blue, light keeps the brand blue.
 */
export function headerLogoColor(theme) {
  return theme === 'dark' ? 'var(--accent-info)' : '#1e40af';
}

// Header Action Button Component
const HeaderButton = ({ icon, badge, onClick, title, ariaExpanded, ariaHaspopup }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={badge > 0 ? `${title}, ${badge} notifications` : title}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        border: 'none',
        background: isHovered ? 'var(--bg-tertiary)' : 'transparent',
        // A text glyph (the apps menu's ⊞) took the button's default black: 1.43:1 on the dark
        // bar. --text-secondary: >= 9.37:1 light, >= 8.33:1 dark, hovered too. Emoji keep their own.
        color: 'var(--text-secondary)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        transition: 'background 0.2s ease',
      }}
    >
      <span aria-hidden="true">{icon}</span>
      {badge > 0 && (
        <span
          style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            minWidth: '16px',
            height: '16px',
            borderRadius: '8px',
            background: 'var(--status-error-solid)',
            color: 'var(--text-on-accent)',
            fontSize: '10px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px',
          }}
          aria-hidden="true"
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
};

// User Dropdown Component
export const UserDropdown = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = (user) => {
    if (!user) return 'U';
    // Handle both firstName/lastName and name formats
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.firstName) return user.firstName[0].toUpperCase();
    if (user.name) return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    if (user.email) return user.email[0].toUpperCase();
    return 'U';
  };

  const getDisplayName = (user) => {
    if (!user) return 'User';
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    if (user.name) return user.name;
    if (user.email) return user.email.split('@')[0];
    return 'User';
  };

  // Lap 2026-10-20: "Profile Settings" (/settings/profile) and "Preferences"
  // (/settings/preferences) went to routes that never existed and rendered the 404. There is no
  // profile or preferences page (each module has its own preferences popover), so they are gone.
  const menuItems = [
    { label: 'Logout', icon: '🚪', action: onLogout, danger: true },
  ];

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={`User menu for ${getDisplayName(user)}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 12px',
          borderRadius: '8px',
          border: '1px solid var(--border-color)',
          background: 'var(--bg-elevated)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--brand-gradient-start) 0%, var(--brand-gradient-end) 100%)',
            color: 'var(--text-on-accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
          aria-hidden="true"
        >
          {getInitials(user)}
        </div>
        <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-secondary)' }}>
          {getDisplayName(user)}
        </span>
        <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }} aria-hidden="true">▼</span>
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '8px',
          minWidth: '200px',
          background: 'var(--bg-elevated)',
          borderRadius: '12px',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--border-color)',
          overflow: 'hidden',
          zIndex: 1001,
        }}>
          {/* User Info Header */}
          <div style={{ padding: '16px', borderBottom: '1px solid var(--border-color)' }}>
            <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
              {getDisplayName(user)}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
              {user?.email || 'user@company.com'}
            </div>
            {user?.role && (
              <div style={{
                fontSize: '11px',
                color: 'var(--accent-primary)',
                marginTop: '4px',
                textTransform: 'capitalize',
                fontWeight: '500'
              }}>
                {user.role}
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div style={{ padding: '8px' }} role="menu" aria-label="User actions">
            {menuItems.map((item, index) => (
              item.divider ? (
                <div key={index} role="separator" style={{ height: '1px', background: 'var(--border-color)', margin: '8px 0' }} />
              ) : (
                <button
                  key={index}
                  role="menuitem"
                  onClick={() => {
                    item.action();
                    setIsOpen(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: item.danger ? 'var(--status-error)' : 'var(--text-secondary)',
                    transition: 'background 0.2s ease',
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'var(--bg-tertiary)'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  <span aria-hidden="true">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Keyboard Shortcuts Modal
const KeyboardShortcutsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const shortcutGroups = [
    {
      title: 'Navigation',
      shortcuts: [
        { keys: ['Ctrl', 'K'], description: 'Open command palette' },
        { keys: ['Ctrl', 'Shift', 'F'], description: 'Global search' },
        { keys: ['Esc'], description: 'Close modal / cancel' },
      ]
    },
    {
      title: 'Quotes',
      shortcuts: [
        { keys: ['Ctrl', 'N'], description: 'New quote (on quotes page)' },
        { keys: ['Ctrl', 'S'], description: 'Save current form' },
        { keys: ['Ctrl', 'P'], description: 'Print / PDF preview' },
      ]
    },
    {
      title: 'Products',
      shortcuts: [
        { keys: ['Ctrl', 'Shift', 'F'], description: 'Search products globally' },
        { keys: ['Enter'], description: 'Confirm inline edit' },
        { keys: ['Esc'], description: 'Cancel inline edit' },
      ]
    }
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'var(--overlay-bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--bg-elevated)', borderRadius: 14, width: '100%', maxWidth: 480,
        boxShadow: 'var(--shadow-md)', maxHeight: '80vh', overflowY: 'auto',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid var(--border-color)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>⌨️</span>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Keyboard Shortcuts</span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, padding: 4,
          }}>✕</button>
        </div>
        <div style={{ padding: '16px 24px' }}>
          {shortcutGroups.map((group, gi) => (
            <div key={gi} style={{ marginBottom: gi < shortcutGroups.length - 1 ? 20 : 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                {group.title}
              </div>
              {group.shortcuts.map((s, si) => (
                <div key={si} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 0', borderBottom: si < group.shortcuts.length - 1 ? '1px solid var(--border-light)' : 'none',
                }}>
                  <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{s.description}</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {s.keys.map((key, ki) => (
                      <kbd key={ki} style={{
                        padding: '2px 8px', background: 'var(--bg-tertiary)', borderRadius: 6, fontSize: 12,
                        fontWeight: 600, color: 'var(--text-secondary)', border: '1px solid var(--input-border)',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                      }}>{key}</kbd>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Contact Support Modal
const ContactSupportModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'var(--overlay-bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--bg-elevated)', borderRadius: 14, width: '100%', maxWidth: 420,
        boxShadow: 'var(--shadow-md)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid var(--border-color)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>💬</span>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Contact Support</span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, padding: 4,
          }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ padding: 16, background: 'var(--status-success-bg)', borderRadius: 10, border: '1px solid var(--status-success-border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--status-success)', marginBottom: 4 }}>Phone Support</div>
            <a href="tel:+19058907171" style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', textDecoration: 'none' }}>
              (905) 890-7171
            </a>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>Mon–Sat 10AM–7PM EST</div>
          </div>
          <div style={{ padding: 16, background: 'var(--status-info-bg)', borderRadius: 10, border: '1px solid var(--status-info-border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--status-info)', marginBottom: 4 }}>Email Support</div>
            <a href="mailto:support@teletime.ca" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none' }}>
              support@teletime.ca
            </a>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>Response within 24 hours</div>
          </div>
          <div style={{ padding: 16, background: 'var(--status-warning-bg)', borderRadius: 10, border: '1px solid var(--status-warning-border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--status-warning)', marginBottom: 4 }}>Store Location</div>
            <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500 }}>Teletime Superstore</div>
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 2 }}>Mississauga, Ontario</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Report a Bug Modal
const ReportBugModal = ({ isOpen, onClose }) => {
  const [bugData, setBugData] = useState({ subject: '', description: '', severity: 'medium' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!bugData.subject.trim() || !bugData.description.trim()) return;
    setSending(true);
    try {
      const mailtoBody = `Severity: ${bugData.severity}\n\nDescription:\n${bugData.description}\n\nPage: ${window.location.href}\nBrowser: ${navigator.userAgent}`;
      window.location.href = `mailto:bugs@teletime.ca?subject=${encodeURIComponent(`[Bug] ${bugData.subject}`)}&body=${encodeURIComponent(mailtoBody)}`;
      setSent(true);
      setTimeout(() => { setSent(false); setBugData({ subject: '', description: '', severity: 'medium' }); onClose(); }, 1500);
    } catch {
      /* fallback — email client will open */
    }
    setSending(false);
  };

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--input-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'var(--overlay-bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--bg-elevated)', borderRadius: 14, width: '100%', maxWidth: 460,
        boxShadow: 'var(--shadow-md)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid var(--border-color)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>🐛</span>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Report a Bug</span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, padding: 4,
          }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {sent ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--status-success)' }}>Bug report prepared!</div>
              <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 4 }}>Your email client should open with the report details.</div>
            </div>
          ) : (
            <>
              <div>
                <label style={labelStyle}>Subject *</label>
                <input style={inputStyle} value={bugData.subject} onChange={e => setBugData(p => ({ ...p, subject: e.target.value }))} placeholder="Brief description of the issue" />
              </div>
              <div>
                <label style={labelStyle}>Severity</label>
                <select style={inputStyle} value={bugData.severity} onChange={e => setBugData(p => ({ ...p, severity: e.target.value }))}>
                  <option value="low">Low — Cosmetic issue</option>
                  <option value="medium">Medium — Feature not working correctly</option>
                  <option value="high">High — Blocking my work</option>
                  <option value="critical">Critical — Data loss or security issue</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>What happened? *</label>
                <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 100 }} value={bugData.description} onChange={e => setBugData(p => ({ ...p, description: e.target.value }))} placeholder="Steps to reproduce, what you expected, what actually happened..." rows={4} />
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-secondary)', padding: '8px 12px', borderRadius: 8 }}>
                Page URL and browser info will be included automatically.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={onClose} style={{ padding: '10px 20px', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleSubmit} disabled={sending || !bugData.subject.trim() || !bugData.description.trim()} style={{
                  padding: '10px 20px', background: (!bugData.subject.trim() || !bugData.description.trim()) ? '#9ca3af' : 'var(--status-error-solid)', color: 'var(--text-on-accent)',
                  border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: (!bugData.subject.trim() || !bugData.description.trim()) ? 'not-allowed' : 'pointer',
                }}>
                  {sending ? 'Sending...' : 'Submit Bug Report'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Help Panel Dropdown
const HelpPanel = ({ isOpen, onClose, dropdownRef, onWhatsNew, onShortcuts, onContact, onBugReport }) => {
  const navigate = useNavigate();

  const helpItems = [
    { icon: '📖', label: 'Documentation', action: () => { navigate('/training-center'); } },
    { icon: '⌨️', label: 'Keyboard Shortcuts', action: onShortcuts },
    { icon: '🆕', label: "What's New", action: onWhatsNew },
    { divider: true },
    { icon: '💬', label: 'Contact Support', action: onContact },
    { icon: '🐛', label: 'Report a Bug', action: onBugReport },
    { divider: true },
    { icon: '🔧', label: 'System Status', action: () => { navigate('/admin/monitoring'); } },
  ];

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      role="menu"
      aria-label="Help and support options"
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '8px',
        width: '220px',
        background: 'var(--bg-elevated)',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-sm)',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
        zIndex: 1001,
      }}
    >
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-color)',
        fontWeight: '600',
        color: 'var(--text-primary)',
      }} id="help-panel-title">
        Help & Support
      </div>
      <div style={{ padding: '8px' }} aria-labelledby="help-panel-title">
        {helpItems.map((item, index) => (
          item.divider ? (
            <div key={index} role="separator" style={{ height: '1px', background: 'var(--border-color)', margin: '8px 0' }} />
          ) : (
            <button
              key={index}
              role="menuitem"
              onClick={() => {
                if (item.action) item.action();
                onClose();
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                border: 'none',
                background: 'transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                color: 'var(--text-secondary)',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <span aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          )
        ))}
      </div>
    </div>
  );
};

// Apps Menu Dropdown
const AppsMenu = ({ isOpen, onClose, dropdownRef }) => {
  const navigate = useNavigate();

  const apps = [
    { icon: '📝', label: 'New Quote', path: '/quotes/new', color: '#3b82f6' },
    { icon: '📦', label: 'Products', path: '/products', color: '#8b5cf6' },
    { icon: '👥', label: 'Customers', path: '/customers', color: '#10b981' },
    { icon: '📊', label: 'Analytics', path: '/analytics', color: '#f59e0b' },
    { icon: '🏪', label: 'Marketplace', path: '/marketplace', color: '#ec4899' },
    { icon: '📋', label: 'Reports', path: '/reports', color: '#6366f1' },
  ];

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      role="menu"
      aria-label="Quick access applications"
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '8px',
        width: '280px',
        background: 'var(--bg-elevated)',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-sm)',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
        zIndex: 1001,
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--border-color)',
          fontWeight: '600',
          color: 'var(--text-primary)',
        }}
        id="apps-menu-title"
      >
        Quick Access
      </div>
      <div
        style={{
          padding: '16px',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
        }}
        aria-labelledby="apps-menu-title"
      >
        {apps.map((app, index) => (
          <button
            key={index}
            role="menuitem"
            aria-label={`Go to ${app.label}`}
            onClick={() => {
              navigate(app.path);
              onClose();
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 8px',
              border: 'none',
              background: 'transparent',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: `${app.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
              }}
              aria-hidden="true"
            >
              {app.icon}
            </div>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '500' }}>
              {app.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Main Header Component
const Header = ({ isMobile }) => {
  const { user, logout, activeLocation } = useAuth();
  // The theme, for the bar's background (light keeps its gradient; dark the elevated token).
  const theme = (useContext(ThemeContext) || {}).theme || 'light';
  const navigate = useNavigate();
  // Below 1,202 px of shell the bar gives its room to the search (tablet pass 2026-10-20, item 4):
  // the "Quotation System" subtitle and the "Working at:" prefix go; the location's name stays.
  const roomy = useViewportWidth() >= RAIL_DEFAULT_BELOW;

  // Dropdown states
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewUnread, setWhatsNewUnread] = useState(getUnreadCount);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [changeLocationOpen, setChangeLocationOpen] = useState(false);
  // The compact search slot is expanded across the bar: the other controls step aside (round 2,
  // P1), so nothing sits under its select or its X.
  const [searchExpanded, setSearchExpanded] = useState(false);
  const aside = searchExpanded ? { visibility: 'hidden' } : null;

  // Refs for click-outside detection
  const helpRef = useRef(null);
  const appsRef = useRef(null);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (helpRef.current && !helpRef.current.contains(event.target)) {
        setHelpOpen(false);
      }
      if (appsRef.current && !appsRef.current.contains(event.target)) {
        setAppsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header
      role="banner"
      aria-label="Main header"
      style={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        height: '64px',
        background: headerBackground(theme),
        boxShadow: 'var(--shadow-sm)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
        zIndex: 1000,
        gap: '16px',
      }}
    >
      {/* Left: Logo and Title */}
      <div data-header-aside="" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexShrink: 0,
        ...aside,
      }}>
        {/* TELETIME Text Logo */}
        <span style={{
          fontSize: '22px',
          fontWeight: '700',
          color: headerLogoColor(theme),
          letterSpacing: '-0.5px',
          lineHeight: 1,
        }}>
          Teletime
        </span>

        {/* Title - Hide on mobile and below 1,202 px (item 4) */}
        {!isMobile && roomy && (
          <div>
            <div style={{
              fontSize: '12px',
              color: 'var(--text-tertiary)',
              fontWeight: '500',
            }}>
              Quotation System
            </div>
          </div>
        )}

        {/* Location Badge */}
        {/* Location: always labelled - the name, or below 1,024 px the store code (item 11). */}
        {activeLocation && (
          <HeaderLocationButton
            location={activeLocation}
            compact={isMobile}
            roomy={roomy}
            onClick={() => setChangeLocationOpen(true)}
          />
        )}
      </div>

      {/* Center: the Z1 search slot (S6) — search, and `>` / Ctrl+K for commands. Sized to its
          room, never under 160 px, else a 44 px Search button that expands across the bar
          (item 4); below 1,024 px always that button (round 2, P1: the old mobile search there
          was a 100 px field whose select and X sat under Notifications and Report a Bug). */}
      <HeaderSearchSlot compact={isMobile} onExpandedChange={setSearchExpanded} />

      {/* Right: Action Buttons */}
      <div data-header-aside="" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flexShrink: 0,
        ...aside,
      }}>
        {/* Notifications */}
        <div style={{ position: 'relative' }}>
          <NotificationBadge
            onClick={() => {
              setNotificationOpen(!notificationOpen);
              setHelpOpen(false);
              setAppsOpen(false);
            }}
            isOpen={notificationOpen}
          />
          <NotificationDropdown
            isOpen={notificationOpen}
            onClose={() => setNotificationOpen(false)}
          />
        </div>

        {/* Report a Bug: docked here at every width (polish 4); it floated over the page's
            controls (the pager's Previous with a tool column open). */}
        <BugReportForm reportedBy={user?.name || user?.email || ''} />

        {/* The assistant, while its floating launcher is hidden (tablet pass, item 1), at every
            width: below 1,024 too (round 2, P2), where its only opener used to be inside More. */}
        <AssistantHeaderButton />

        {!isMobile && (
          <>
            {/* Help */}
            <div ref={helpRef} style={{ position: 'relative' }}>
              <HeaderButton
                icon="❓"
                badge={whatsNewUnread > 0 ? whatsNewUnread : undefined}
                title="Help"
                ariaExpanded={helpOpen}
                ariaHaspopup="menu"
                onClick={() => {
                  setHelpOpen(!helpOpen);
                  setNotificationOpen(false);
                  setAppsOpen(false);
                }}
              />
              <HelpPanel
                isOpen={helpOpen}
                onClose={() => setHelpOpen(false)}
                dropdownRef={helpRef}
                onWhatsNew={() => {
                  setHelpOpen(false);
                  setWhatsNewOpen(true);
                }}
                onShortcuts={() => {
                  setHelpOpen(false);
                  setShortcutsOpen(true);
                }}
                onContact={() => {
                  setHelpOpen(false);
                  setContactOpen(true);
                }}
                onBugReport={() => {
                  setHelpOpen(false);
                  setBugReportOpen(true);
                }}
              />
            </div>

            {/* Apps */}
            <div ref={appsRef} style={{ position: 'relative' }}>
              <HeaderButton
                icon="⊞"
                title="Quick Access Apps"
                ariaExpanded={appsOpen}
                ariaHaspopup="menu"
                onClick={() => {
                  setAppsOpen(!appsOpen);
                  setNotificationOpen(false);
                  setHelpOpen(false);
                }}
              />
              <AppsMenu
                isOpen={appsOpen}
                onClose={() => setAppsOpen(false)}
                dropdownRef={appsRef}
              />
            </div>
          </>
        )}

        {/* Theme Toggle */}
        <ThemeToggle size="default" />

        <div style={{ width: '1px', height: '24px', background: 'var(--border-color)', margin: '0 8px' }} />
        <UserDropdown user={user} onLogout={handleLogout} />
      </div>

      {/* What's New Panel */}
      <WhatsNewPanel
        isOpen={whatsNewOpen}
        onClose={() => {
          setWhatsNewOpen(false);
          setWhatsNewUnread(getUnreadCount());
        }}
      />

      {/* Help Modals */}
      <KeyboardShortcutsModal isOpen={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <ContactSupportModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
      <ReportBugModal isOpen={bugReportOpen} onClose={() => setBugReportOpen(false)} />

      {/* Change Location Modal */}
      <ChangeLocationModal isOpen={changeLocationOpen} onClose={() => setChangeLocationOpen(false)} />
    </header>
  );
};

export default Header;
